/*
 * Copyright (c) 2022 biocrates life sciences ag, All rights reserved.
 */

import axios, {AxiosInstance} from "axios"
import {combineEpics, Epic, ofType} from "redux-observable"
import {from, of} from "rxjs"
import {catchError, map, switchMap} from "rxjs/operators"
import {getType} from "typesafe-actions"

import {errorAction} from "../../core/actions/core.actions"
import {
    getGroupSettingsAction,
    getGroupSettingsSuccessAction,
    getSettingAction,
    getSettingSuccessAction,
    updateSettingAction,
    updateSettingSuccessAction,
} from "../actions/settings.actions"
import Setting from "../models/setting"
import SettingMetadata from "../models/settingMetadata"
import {SettingResponse} from "../models/settingResponse"

const DEFAULT_API_SETTINGS_BASE_URL = "/api/settings"
const SETTING_KEY_NAME_MAX_LENGTH = 20
const SETTING_ERROR_MSG = `Invalid setting group/key length (min 1, max ${SETTING_KEY_NAME_MAX_LENGTH})`

// Group or Key length validator
const isValidSettingProperty = (property: string) =>
    property && property.length <= SETTING_KEY_NAME_MAX_LENGTH

// Handlers to be used in AAL (redux-thunk)

export const getSettingActionHandler = (
    baseUrl: string,
    instance: AxiosInstance,
    settingMetadata: SettingMetadata<any, any>
) =>
    !isValidSettingProperty(settingMetadata.group) ||
    !isValidSettingProperty(settingMetadata.key)
        ? of(errorAction(new Error(SETTING_ERROR_MSG)))
        : from(
              instance.get<SettingResponse>(
                  `${baseUrl}/get/${settingMetadata.group}/${settingMetadata.key}`
              )
          ).pipe(
              map(({data}) => ({
                  ...data,
                  value: JSON.parse(data.value) ?? undefined,
              })),
              map((setting) => getSettingSuccessAction(setting)),
              catchError((err) => of(errorAction(err)))
          )

export const getGroupSettingsActionHandler = (
    baseUrl: string,
    instance: AxiosInstance,
    group: string
) =>
    !isValidSettingProperty(group)
        ? of(errorAction(new Error(SETTING_ERROR_MSG)))
        : from(instance.get<SettingResponse[]>(`${baseUrl}/get/${group}`)).pipe(
              map(({data}) => ({
                  group,
                  value: Object.assign(
                      {},
                      ...data.map((s) => ({[s.key]: JSON.parse(s.value)}))
                  ),
              })),
              map((groupSettings) =>
                  getGroupSettingsSuccessAction(groupSettings)
              ),
              catchError((err) => of(errorAction(err)))
          )

export const updateSettingActionHandler = (
    baseUrl: string,
    instance: AxiosInstance,
    setting: Setting<any, any, any>
) =>
    !isValidSettingProperty(setting.group) ||
    !isValidSettingProperty(setting.key)
        ? of(errorAction(new Error(SETTING_ERROR_MSG)))
        : from(
              instance.put(
                  `${baseUrl}/save/${setting.group}/${setting.key}`,
                  setting.value
              )
          ).pipe(
              map(() => updateSettingSuccessAction()),
              catchError((err) => of(errorAction(err)))
          )

// Epics to be used in WebIDQ & QMDB (redux-observable)

const getSetting: Epic = (action$) =>
    action$.pipe(
        ofType(getType(getSettingAction)),
        switchMap((action) =>
            getSettingActionHandler(
                DEFAULT_API_SETTINGS_BASE_URL,
                axios,
                action.payload
            )
        )
    )

const getGroupSettings: Epic = (action$) =>
    action$.pipe(
        ofType(getType(getGroupSettingsAction)),
        switchMap((action) =>
            getGroupSettingsActionHandler(
                DEFAULT_API_SETTINGS_BASE_URL,
                axios,
                action.payload
            )
        )
    )

const updateSetting: Epic = (action$) =>
    action$.pipe(
        ofType(getType(updateSettingAction)),
        switchMap((action) =>
            updateSettingActionHandler(
                DEFAULT_API_SETTINGS_BASE_URL,
                axios,
                action.payload
            )
        )
    )

export default combineEpics(getSetting, getGroupSettings, updateSetting)
